import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import hotel1 from '../Assets/Images/hotelblog.jpg';
import HomepageHotelCard from './HomepageHotelCard';
import api from '../config/api';
import { API_URLS } from '../config/urls';

const extractPrice = (rooms, type) => {
    if (!rooms || rooms.length === 0) return 0;
    const lowest = Math.min(...rooms.map(r => Number(r.base_price_per_night || 0)));
    return type === 'price' ? lowest : lowest + (lowest * 0.2);
};

const TopRatedHotels = () => {
    const navigate = useNavigate();
    const [hotels, setHotels] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchTopRated = async () => {
            try {
                const res = await api.get(API_URLS.TOP_RATED_PROPERTIES);
                if (res.data?.success && Array.isArray(res.data.data)) {
                    // Filter to strictly show RateHawk hotels only
                    const rhHotels = res.data.data.filter(hotel => 
                        hotel && (hotel.provider === 'ratehawk' || String(hotel.id).startsWith('rh_'))
                    );
                    setHotels(rhHotels);
                }
            } catch (error) {
                console.error("Failed to fetch top rated properties", error);
            } finally {
                setLoading(false);
            }
        };
        fetchTopRated();
    }, []);

    const getHotelImage = (hotel) => {
        if (hotel.images && hotel.images.length > 0) {
            const first = hotel.images[0];
            if (typeof first === 'string') return first;
            if (first?.file_path) {
                return `${process.env.REACT_APP_STORAGE_URL || (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/api\/v1\/?$/, '') + '/storage'}/${first.file_path}`;
            }
        }
        return hotel1;
    };

    const getHotelPrice = (hotel) => {
        if (hotel.min_price) return Number(hotel.min_price);
        return extractPrice(hotel.rooms, 'price') || 3499;
    };

    const getHotelOriginalPrice = (hotel) => {
        if (hotel.min_price) return Math.round(Number(hotel.min_price) * 1.2);
        return extractPrice(hotel.rooms, 'original') || 4199;
    };

    const handleCardClick = (hotel) => {
        navigate(`/hotel/${hotel.id}`, { state: { isSoldOut: hotel.is_sold_out === true } });
    };

    const handleBookNow = (hotelId) => {
        navigate(`/hotel/${hotelId}`);
    };

    if (loading || hotels.length === 0) return null;

    return (
        <section className="py-md-5 py-3">
            <div className="container">
                {/* Title */}
                <h2 className="fw-bold text-md-center text-start offer-title mb-3 d-none d-md-block" style={{ fontSize: '2rem' }}>
                    Top Rated Hotels
                </h2>
                <h2 className="fw-bold text-start mb-3 d-block d-md-none" style={{ fontSize: '20px' }}>
                    Top Rated Hotels
                </h2>
                <p className="text-muted text-center mb-4 d-none d-md-block">
                    Explore top Rated Hotels for your next trip
                </p>

                {/* Mobile Slider - Hidden on Desktop */}
                <div className="d-block d-lg-none mb-4">
                    <div className="hotel-slider-wrapper">
                        <div
                            className="d-flex gap-3 hotel-slider px-3"
                            style={{
                                overflowX: 'auto',
                                scrollSnapType: 'x mandatory',
                                WebkitOverflowScrolling: 'touch',
                                scrollbarWidth: 'none',
                                msOverflowStyle: 'none',
                                paddingBottom: '10px'
                            }}
                        >
                            {hotels.map((hotel) => {
                                const price = getHotelPrice(hotel);
                                const originalPrice = getHotelOriginalPrice(hotel);
                                const discountPercent = Math.round(((originalPrice - price) / originalPrice) * 100) || 17;

                                return (
                                    <div
                                        key={hotel.id}
                                        className="flex-shrink-0"
                                        style={{
                                            width: '280px',
                                            scrollSnapAlign: 'start'
                                        }}
                                    >
                                        <HomepageHotelCard
                                            id={hotel.id}
                                            image={getHotelImage(hotel)}
                                            title={hotel.name}
                                            location={hotel.address ? `${hotel.address}, ${hotel.city}` : `${hotel.city}, ${hotel.state || ''}`}
                                            rating={Number(hotel.star_rating) || 4.5}
                                            price={price}
                                            originalPrice={originalPrice}
                                            discountPercent={discountPercent}
                                            isSoldOut={hotel.is_sold_out || false}
                                            onCardClick={() => handleCardClick(hotel)}
                                            onBookNow={() => handleBookNow(hotel.id)}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Desktop Grid - Hidden on Mobile */}
                <div className="d-none d-lg-block mb-4">
                    <div className="row">
                        {hotels.map((hotel) => {
                            const price = getHotelPrice(hotel);
                            const originalPrice = getHotelOriginalPrice(hotel);
                            const discountPercent = Math.round(((originalPrice - price) / originalPrice) * 100) || 17;

                            return (
                                <div key={hotel.id} className="col-6 col-xl-4 mb-4">
                                    <HomepageHotelCard
                                        id={hotel.id}
                                        image={getHotelImage(hotel)}
                                        title={hotel.name}
                                        location={hotel.address ? `${hotel.address}, ${hotel.city}` : `${hotel.city}, ${hotel.state || ''}`}
                                        rating={Number(hotel.star_rating) || 4.5}
                                        price={price}
                                        originalPrice={originalPrice}
                                        discountPercent={discountPercent}
                                        isSoldOut={hotel.is_sold_out || false}
                                        onCardClick={() => handleCardClick(hotel)}
                                        onBookNow={() => handleBookNow(hotel.id)}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* View All Button */}
                <div className="text-center">
                    <a href="/search" className="btn btn-outline-success px-4 py-2 rounded-pill fw-semibold">
                        View All Hotels
                    </a>
                </div>
            </div>

            {/* Custom CSS for smooth scrolling */}
            <style dangerouslySetInnerHTML={{ __html: `
                .hotel-slider::-webkit-scrollbar {
                    display: none;
                }
                
                .hotel-slider {
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                }

                @media (max-width: 991.98px) {
                    .hotel-slider {
                        padding-bottom: 10px;
                    }
                }
            `}} />
        </section>
    );
};

export default TopRatedHotels;